
// FormularioPlanes.jsx
import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card, CardBody, Row, Col, Label, Button, Alert, Collapse, Input } from 'reactstrap';
import CoberturaModal from './CoberturaModal';
import AsistenciaModal from './AsistenciaModal';
import { urlListarAsistenciasAp } from '../../constants/urls';
import { toNumber, isEmptyObject, mapOptions, sumByPrima, sameCoverageKey, formatTasa } from './utils/planesUtils';

/* ===================== Modelo ===================== */
const defaultPlan = {
  nombre: '',
  producto: '',
  primaNeta: '',
  primaTotal: '',
  dependeDeOtroGratuidad: 'no',   // 'si' | 'no'
  dependeDeOtroAplica: 'no',   // 'si' | 'no'
  afectaAPlanGratuidad: '',
  afectaAPlanAplica: '',
  tipoCondicional: '',
  valorCondicional: '',
  coberturas: [],
  asistencias: []
};

const ageIsValid = (v) => v !== '' && !isNaN(Number(v)) && Number(v) >= 1 && Number(v) <= 99;

const FormularioPlanes = forwardRef(({
  planIndividual, onSubmit, values, urlCoberturas, urlAsistencias, ramo, subRamo, producto, tipoEmision,
  edadMinimaGlobal, edadMaximaGlobal, onEdadGlobalChange, productosOptions
}, ref) => {
  const isMultiple = !planIndividual;
  const isProductoMode = tipoEmision === 'producto';

  const [planes, setPlanes] = useState([{ ...defaultPlan }]);
  const [errors, setErrors] = useState([]);
  const [erroresEdad, setErroresEdad] = useState({});
  const [showAlert, setShowAlert] = useState(false);
  const [collapseStates, setCollapseStates] = useState([true]);

  // Asistencias globales (solo si es múltiple)
  const [asistenciasGlobal, setAsistenciasGlobal] = useState([]);

  // Resumen global (todos los planes)
  const [resumenPlanes, setResumenPlanes] = useState({ subtotal: '0.00', impuestos: '0.00', total: '0.00' });
  const [impuestosOpen, setImpuestosOpen] = useState(false);
  const [impuestosDetalle, setImpuestosDetalle] = useState({ base: '0.00', superintendencia35: '0.00', campesino05: '0.00', total4: '0.00' });

  // Per-plan auto-load states (product mode)
  const [planCoberturasLoading, setPlanCoberturasLoading] = useState([false]);
  const [planCoberturasError, setPlanCoberturasError] = useState([null]);

  // Estado para modales
  const [modalCoberturaOpen, setModalCoberturaOpen] = useState(false);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [isEditCobertura, setIsEditCobertura] = useState(false);
  const [indexCoberturaEdit, setIndexCoberturaEdit] = useState(null);
  const [nuevaCobertura, setNuevaCobertura] = useState({ cod_riesgo: '', nombre: '', sumaAsegurada: '', tasa: '', prima: '', deducible: 'SIN DEDUCIBLE', cod_deducible: '' });
  const [coberturasOptions, setCoberturasOptions] = useState([]);
  const [deduciblesOptions, setDeduciblesOptions] = useState([]);
  const [coberturasLoading, setCoberturasLoading] = useState(false);
  const [coberturasError, setCoberturasError] = useState('');
  const [erroresCoberturaValidacion, setErroresCoberturaValidacion] = useState({});

  const [modalAsistenciaOpen, setModalAsistenciaOpen] = useState(false);
  const [isEditAsistencia, setIsEditAsistencia] = useState(false);
  const [indexAsistenciaEdit, setIndexAsistenciaEdit] = useState(null);
  const [nuevaAsistencia, setNuevaAsistencia] = useState({ codigo: '', nombre: '', valor: '' });
  const [asistenciasOptions, setAsistenciasOptions] = useState([]);
  const [asistenciasLoading, setAsistenciasLoading] = useState(false);
  const [asistenciasError, setAsistenciasError] = useState('');
  const [erroresAsistenciaValidacion, setErroresAsistenciaValidacion] = useState({});

  /* ===================== Calcular totales por plan ===================== */
  const recomputeTotals = (planIdx, base = null, noSet = false) => {
    const cur = base ? [...base] : [...planes];
    const cobTotal = sumByPrima(cur[planIdx].coberturas);
    const primaNetaNum = cobTotal;
    const primaTotalNum = cobTotal * 1.04;

    cur[planIdx].primaNeta = primaNetaNum.toFixed(2);
    cur[planIdx].primaTotal = primaTotalNum.toFixed(2);

    if (!noSet) setPlanes(cur);
    return cur;
  };

  /* ===================== Carga inicial desde props.values ===================== */
  useEffect(() => {
    if (values && Array.isArray(values) && values.length > 0) {
      const norm = values.map(v => ({
        ...defaultPlan,
        ...v,
        dependeDeOtroGratuidad: v && v.dependeDeOtroGratuidad ? v.dependeDeOtroGratuidad : 'no',
        dependeDeOtroAplica: v && v.dependeDeOtroAplica ? v.dependeDeOtroAplica : 'no',
        afectaAPlanGratuidad: v && v.afectaAPlanGratuidad ? v.afectaAPlanGratuidad : '',
        afectaAPlanAplica: v && v.afectaAPlanAplica ? v.afectaAPlanAplica : ''
      }));

      let cur = norm;
      for (let i = 0; i < cur.length; i++) cur = recomputeTotals(i, cur, true);

      setPlanes(cur);
      setErrors(cur.map(() => ({})));
      setCollapseStates(cur.map((_, i) => i === cur.length - 1));
      setPlanCoberturasLoading(cur.map(() => false));
      setPlanCoberturasError(cur.map(() => null));

      if (isMultiple) setAsistenciasGlobal(Array.isArray(norm[0].asistencias) ? norm[0].asistencias : []);

      // Restore global ages from first plan when loading saved data (non-product mode)
      if (!isProductoMode && onEdadGlobalChange) {
        const min = String(norm[0].edadMinima || '');
        const max = String(norm[0].edadMaxima || '');
        if (min || max) onEdadGlobalChange(min, max);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  /* ===================== Cargar Coberturas (opciones para modal manual) ===================== */
  useEffect(() => {
    const fetchCoberturas = async () => {
      setCoberturasError('');
      setCoberturasOptions([]);
      if (!ramo || !subRamo || isEmptyObject(ramo) || isEmptyObject(subRamo)) return;

      setCoberturasLoading(true);
      try {
        const bodyPayload = { ramo: ramo.value, subRamo: subRamo.value, producto: (producto && producto.value != null) ? producto.value : producto };
        const response = await fetch(urlCoberturas, { method: 'POST', body: JSON.stringify(bodyPayload) });
        const json = await response.json();
        console.log("🚀 ~ fetchCoberturas ~ json:", json)

        if (json && json.transaccion) {
          if (json.deducible && Array.isArray(json.deducible)) {
            var optsDed = json.deducible.map(function (d) {
              return { value: String(d.dedCodigoCore), label: d.dedUnificado, raw: d };
            });
            setDeduciblesOptions(optsDed);
          } else {
            setDeduciblesOptions([
              { value: '2', label: '2 - SIN DEDUCIBLE', raw: { dedCodigoCore: '2', dedDescripcion: 'SIN DEDUCIBLE' } }
            ]);
          }

          var list = Array.isArray(json.coberturas) ? json.coberturas : [];
          var seen = {};
          var opts = list
            .filter(function (c) { var nombre = c && c.Cobertura != null ? String(c.Cobertura) : ''; nombre = nombre.trim().toUpperCase(); return nombre !== '' && nombre !== 'ASISTENCIAS'; })
            .map(function (c) { var value = c && c.cod_riesgo != null ? String(c.cod_riesgo) : ''; var label = c && c.Cobertura != null ? String(c.Cobertura) : ''; return { value: value, label: label, raw: c }; })
            .filter(function (o) { return o.value && o.label; })
            .filter(function (o) { var k = o.value + '|' + o.label; if (seen[k]) return false; seen[k] = true; return true; })
            .sort(function (a, b) { return a.label.localeCompare(b.label, 'es'); });
          setCoberturasOptions(opts);
        } else {
          setCoberturasError((json && json.mensaje) ? json.mensaje : 'No se pudieron obtener coberturas.');
        }
      } catch (e) {
        setCoberturasError('Error al cargar coberturas');
      } finally {
        setCoberturasLoading(false);
      }
    };
    fetchCoberturas();
  }, [ramo, subRamo, producto, urlCoberturas]);

  /* ===================== Cargar Asistencias ===================== */
  useEffect(() => {
    const endpointAsist = urlAsistencias || urlListarAsistenciasAp;
    if (!endpointAsist) return;

    const fetchAsistencias = async () => {
      setAsistenciasError('');
      setAsistenciasOptions([]);

      try {
        setAsistenciasLoading(true);
        const response = await fetch(endpointAsist, { method: 'POST', body: JSON.stringify({ ramo: ramo && ramo.value ? ramo.value : '', subRamo: subRamo && subRamo.value ? subRamo.value : '' }) });
        const json = await response.json();
        if (json && json.transaccion) {
          const opts = mapOptions(json.asistencias, ['value', 'codigo'], ['label', 'nombre']);
          setAsistenciasOptions(opts);
        } else {
          setAsistenciasError((json && json.mensaje) ? json.mensaje : 'No se pudieron obtener asistencias.');
        }
      } catch (e) {
        setAsistenciasError('Error al cargar asistencias');
      }
      setAsistenciasLoading(false);
    };
    fetchAsistencias();
  }, [ramo, subRamo, urlAsistencias]);

  /* ===================== Resumen Global ===================== */
  useEffect(() => {
    const sumPrimaNetaPlanes = (planes || []).reduce((acc, p) => acc + toNumber(p.primaNeta), 0);
    const sumAsistencias = isMultiple ? sumByPrima(asistenciasGlobal) : (planes || []).reduce((acc, p) => acc + sumByPrima(p.asistencias), 0);
    const baseImpuesto = sumPrimaNetaPlanes + sumAsistencias;
    const subtotalNum = sumPrimaNetaPlanes + sumAsistencias;
    const impuestosNum = baseImpuesto * 0.04;
    const totalNum = subtotalNum + impuestosNum;

    setResumenPlanes({ subtotal: subtotalNum.toFixed(2), impuestos: impuestosNum.toFixed(2), total: totalNum.toFixed(2) });

    const s35 = baseImpuesto * 0.035; const c05 = baseImpuesto * 0.005; const t4 = s35 + c05;
    setImpuestosDetalle({ base: baseImpuesto.toFixed(2), superintendencia35: s35.toFixed(2), campesino05: c05.toFixed(2), total4: t4.toFixed(2) });
  }, [planes, asistenciasGlobal, isMultiple]);

  /* ===================== Auto-cargar coberturas por producto/plan ===================== */
  const cargarCoberturasPorProducto = async (planIdx, planProducto, edadMin, edadMax) => {
    if (!isProductoMode) return;
    if (!ramo || !subRamo || isEmptyObject(ramo) || isEmptyObject(subRamo)) return;
    if (!planProducto || String(planProducto).trim() === '') return;

    setPlanCoberturasLoading(prev => { const n = [...prev]; n[planIdx] = true; return n; });
    setPlanCoberturasError(prev => { const n = [...prev]; n[planIdx] = null; return n; });

    try {
      const planActual = planes[planIdx];
      const bodyPayload = {
        ramo: ramo.value,
        subRamo: subRamo.value,
        producto: String(planProducto),
        planNombre: planActual ? (planActual.nombre || '') : '',
        edadMinima: edadMin,
        edadMaxima: edadMax
      };
      const response = await fetch(urlCoberturas, { method: 'POST', body: JSON.stringify(bodyPayload) });
      const json = await response.json();

      if (json && json.transaccion) {
        const list = Array.isArray(json.coberturas) ? json.coberturas : [];
        const coberturas = list
          .filter(c => c && (c.Cobertura || c.nombre) && String(c.Cobertura || c.nombre).trim().toUpperCase() !== 'ASISTENCIAS')
          .map(c => ({
            cod_riesgo: c.cod_riesgo ? String(c.cod_riesgo) : '',
            nombre: c.nombre || (c.Cobertura ? String(c.Cobertura) : ''),
            sumaAsegurada: c.sumaAsegurada != null ? String(c.sumaAsegurada) : '',
            tasa: formatTasa(c.tasa || ''),
            prima: toNumber(c.prima, 0),
            deducible: c.deducible || 'SIN DEDUCIBLE',
            cod_deducible: c.cod_deducible ? String(c.cod_deducible) : '2',
            responsabilidad: 0,
            cod_ramo_tec: c.cod_ramo_tec ? String(c.cod_ramo_tec) : (ramo && ramo.value != null ? String(ramo.value) : ''),
            cod_subramo_tec: c.cod_subramo_tec ? String(c.cod_subramo_tec) : (subRamo && subRamo.value != null ? String(subRamo.value) : ''),
            sn_acumula_suma_total: c.sn_acumula_suma_total != null ? c.sn_acumula_suma_total : 0,
            sn_acumula_prima: c.sn_acumula_prima != null ? c.sn_acumula_prima : 0
          }));

        setPlanes(prev => {
          const updated = [...prev];
          if (!updated[planIdx]) return prev;
          const cobTotal = coberturas.reduce((sum, c) => sum + toNumber(c.prima), 0);
          updated[planIdx] = {
            ...updated[planIdx],
            coberturas,
            primaNeta: cobTotal.toFixed(2),
            primaTotal: (cobTotal * 1.04).toFixed(2)
          };
          return updated;
        });

        if (coberturas.length === 0) {
          setPlanCoberturasError(prev => { const n = [...prev]; n[planIdx] = 'No existen coberturas configuradas para el producto seleccionado'; return n; });
        }
      } else {
        setPlanes(prev => {
          const updated = [...prev];
          if (!updated[planIdx]) return prev;
          updated[planIdx] = { ...updated[planIdx], coberturas: [], primaNeta: '0.00', primaTotal: '0.00' };
          return updated;
        });
        setPlanCoberturasError(prev => {
          const n = [...prev];
          n[planIdx] = 'No existen coberturas configuradas para el producto seleccionado';
          return n;
        });
      }
    } catch (e) {
      setPlanCoberturasError(prev => { const n = [...prev]; n[planIdx] = 'Error al cargar coberturas del producto'; return n; });
    } finally {
      setPlanCoberturasLoading(prev => { const n = [...prev]; n[planIdx] = false; return n; });
    }
  };

  /* ===================== Normalización de Cobertura con Backfill de Deducible ===================== */
  const normalizarCobertura = (c) => {
    const cod = c.cod_deducible || '';
    const desc = c.deducible || 'SIN DEDUCIBLE';

    if (cod && cod !== '') return { ...c, cod_deducible: String(cod), deducible: desc, tasa: formatTasa(c.tasa) };

    let finalCod = '2';
    if (desc.toUpperCase() !== 'SIN DEDUCIBLE') {
      const opt = (deduciblesOptions || []).find(o =>
        (o && o.raw && o.raw.dedDescripcion && String(o.raw.dedDescripcion).toUpperCase() === desc.toUpperCase()) ||
        (o && o.label && String(o.label).toUpperCase().includes(desc.toUpperCase()))
      );
      if (opt) finalCod = String(opt.value);
    }

    return {
      ...c,
      cod_deducible: finalCod,
      deducible: desc,
      tasa: formatTasa(c.tasa)
    };
  };

  /* ===================== Exponer métodos al padre ===================== */
  useImperativeHandle(ref, () => ({
    obtenerDatos: (skipValidation = false) => {
      if (skipValidation) {
        if (isMultiple) return planes.map(p => ({ ...p, asistencias: [...asistenciasGlobal] }));
        return planes;
      }

      if (validar()) {
        setShowAlert(false);
        const normalizarPlanes = (plist) => plist.map(p => ({
          ...p,
          coberturas: (p.coberturas || []).map(normalizarCobertura)
        }));

        if (isMultiple) {
          const withAsist = normalizarPlanes(planes).map(p => ({ ...p, asistencias: [...asistenciasGlobal] }));
          return withAsist;
        }
        return normalizarPlanes(planes);
      } else {
        setShowAlert(true);
        return null;
      }
    },
    obtenerDatosDetallados: () => {
      if (!validar()) { setShowAlert(true); return null; }

      const planesNormalizados = isMultiple ? planes.map(p => ({ ...p, asistencias: [...asistenciasGlobal] })) : planes;

      const planesDetallados = (planesNormalizados || []).map((p, idx) => {
        const primaNeta = toNumber(p.primaNeta);
        const primaTotal = toNumber(p.primaTotal);
        const impuestos4 = +(primaTotal - primaNeta).toFixed(2);
        const totalAsistencias = sumByPrima(p.asistencias);
        const totalPlanPersona = +(primaTotal + totalAsistencias).toFixed(2);

        return {
          indice: idx + 1,
          nombre: p.nombre || '',
          dependeDeOtroGratuidad: p.dependeDeOtroGratuidad || 'no',
          dependeDeOtroAplica: p.dependeDeOtroAplica || 'no',
          afectaAPlanGratuidad: p.afectaAPlanGratuidad || '',
          afectaAPlanAplica: p.afectaAPlanAplica || '',
          tipoCondicional: p.tipoCondicional || '',
          valorCondicional: p.valorCondicional || '',
          coberturas: Array.isArray(p.coberturas) ? p.coberturas.map(c => {
            const normalized = normalizarCobertura(c);
            return {
              cod_riesgo: normalized.cod_riesgo || '',
              nombre: normalized.nombre || '',
              sumaAsegurada: normalized.sumaAsegurada || '',
              tasa: formatTasa(normalized.tasa) || '0',
              prima: toNumber(normalized.prima),
              deducible: normalized.deducible,
              cod_deducible: normalized.cod_deducible,
              responsabilidad: 0
            };
          }) : [],
          asistencias: Array.isArray(p.asistencias) ? p.asistencias.map(a => ({
            codigo: a.codigo || '',
            nombre: a.nombre || '',
            prima: toNumber(a.prima)
          })) : [],
          totales: {
            primaNeta: +primaNeta.toFixed(2),
            impuestos4: +impuestos4.toFixed(2),
            primaTotal: +primaTotal.toFixed(2),
            asistencias: +totalAsistencias.toFixed(2),
            totalPlanPersona
          }
        };
      });

      const sumPrimaTotal = planesDetallados.reduce((acc, p) => acc + toNumber(p.totales.primaTotal), 0);
      const sumPrimaNeta = planesDetallados.reduce((acc, p) => acc + toNumber(p.totales.primaNeta), 0);
      const sumAsist = planesDetallados.reduce((acc, p) => acc + toNumber(p.totales.asistencias), 0);
      const impuestosPersona = +(sumPrimaTotal - sumPrimaNeta).toFixed(2);
      const subtotalPersona = +(sumPrimaTotal + sumAsist).toFixed(2);
      const totalPersona = subtotalPersona;

      const totalesPorPlan = planesDetallados.map(p => ({
        nombre: p.nombre,
        primaNeta: p.totales.primaNeta,
        impuestos4: p.totales.impuestos4,
        primaTotal: p.totales.primaTotal,
        asistencias: p.totales.asistencias,
        totalPlanPersona: p.totales.totalPlanPersona
      }));

      const totalesGlobales = { subtotalPersona: +subtotalPersona.toFixed(2), impuestosPersona: +impuestosPersona.toFixed(2), totalPersona: +totalPersona.toFixed(2) };

      return {
        planIndividual: !!planIndividual,
        planes: planesNormalizados,
        planesDetallados,
        totalesPorPlan,
        totalesGlobales,
        asistenciasGlobal: isMultiple ? [...asistenciasGlobal] : null,
        resumenUi: {
          subtotal: +toNumber(resumenPlanes.subtotal).toFixed(2),
          impuestos: +toNumber(resumenPlanes.impuestos).toFixed(2),
          total: +toNumber(resumenPlanes.total).toFixed(2)
        },
        impuestosDetalle: { ...impuestosDetalle }
      };
    }
  }));

  /* ===================== Handlers generales ===================== */
  const toggleCollapse = (index) => setCollapseStates(prev => prev.map((val, i) => i === index ? !val : val));

  const handleChange = (index, field, value) => {
    const prevNombre = planes[index].nombre;
    const updated = [...planes];
    updated[index][field] = value;
    setPlanes(updated);

    // Trigger auto-load when plan product changes in product mode (requires plan name)
    if (field === 'producto' && isProductoMode && value && String(value).trim() !== '') {
      if (ageIsValid(edadMinimaGlobal) && ageIsValid(edadMaximaGlobal)) {
        if (updated[index].nombre && updated[index].nombre.trim() !== '') {
          cargarCoberturasPorProducto(index, value, edadMinimaGlobal, edadMaximaGlobal);
        }
      }
    }

    // Trigger auto-load when name is entered for the first time and product is already selected in product mode
    if (field === 'nombre' && isProductoMode) {
      const wasEmpty = (prevNombre || '').trim() === '';
      const isNowNonEmpty = (value || '').trim() !== '';
      if (wasEmpty && isNowNonEmpty) {
        const p = updated[index];
        if (p.producto && String(p.producto).trim() !== '') {
          if (ageIsValid(edadMinimaGlobal) && ageIsValid(edadMaximaGlobal)) {
            cargarCoberturasPorProducto(index, p.producto, edadMinimaGlobal, edadMaximaGlobal);
          }
        }
      }
    }
  };

  const handleEdadGlobalChange = (min, max) => {
    if (onEdadGlobalChange) onEdadGlobalChange(min, max);

    // Re-trigger auto-load for plans that have a product and name entered when ages change
    if (isProductoMode && ageIsValid(min) && ageIsValid(max)) {
      planes.forEach((p, idx) => {
        if (p.producto && String(p.producto).trim() !== '' && p.nombre && p.nombre.trim() !== '') {
          cargarCoberturasPorProducto(idx, p.producto, min, max);
        }
      });
    }
  };

  const agregarPlan = () => {
    const nuevo = { ...defaultPlan };
    const updated = [...planes, nuevo];
    setPlanes(updated);
    setErrors(prev => [...prev, {}]);
    setCollapseStates(prev => prev.map(() => false).concat(true));
    setPlanCoberturasLoading(prev => [...prev, false]);
    setPlanCoberturasError(prev => [...prev, null]);
    const idxNuevo = updated.length - 1;
    const tmp = recomputeTotals(idxNuevo, updated, true);
    setPlanes([...tmp]);
  };

  const eliminarPlan = (index) => {
    if (planes.length === 1) {
      setPlanes([{ ...defaultPlan }]);
      setErrors([{}]);
      setCollapseStates([true]);
      setPlanCoberturasLoading([false]);
      setPlanCoberturasError([null]);
      if (isMultiple) setAsistenciasGlobal([]);
    } else {
      const updated = [...planes]; updated.splice(index, 1); setPlanes(updated);
      const updatedErrors = [...errors]; updatedErrors.splice(index, 1); setErrors(updatedErrors);
      let updatedCollapse = [...collapseStates]; updatedCollapse.splice(index, 1); updatedCollapse = updatedCollapse.map(() => false); if (updatedCollapse.length > 0) updatedCollapse[updatedCollapse.length - 1] = true; setCollapseStates(updatedCollapse);
      const updatedLoading = [...planCoberturasLoading]; updatedLoading.splice(index, 1); setPlanCoberturasLoading(updatedLoading);
      const updatedErr = [...planCoberturasError]; updatedErr.splice(index, 1); setPlanCoberturasError(updatedErr);
    }
  };

  /* ===================== Coberturas (abrir/guardar/eliminar) ===================== */
  const abrirModalAgregarCobertura = (planIdx) => { setPlanSeleccionado(planIdx); setIsEditCobertura(false); setIndexCoberturaEdit(null); setNuevaCobertura({ cod_riesgo: '', nombre: '', sumaAsegurada: '', tasa: '', prima: '', deducible: 'SIN DEDUCIBLE', cod_deducible: '2' }); setCoberturasError(''); setErroresCoberturaValidacion({}); setModalCoberturaOpen(true); };
  const abrirModalEditarCobertura = (planIdx, covIdx) => { const cov = (planes[planIdx].coberturas || [])[covIdx] || {}; setPlanSeleccionado(planIdx); setIsEditCobertura(true); setIndexCoberturaEdit(covIdx); setNuevaCobertura({ ...cov, sumaAsegurada: cov.sumaAsegurada != null ? String(cov.sumaAsegurada) : '', prima: cov.prima != null ? String(cov.prima) : '', tasa: cov.tasa != null ? formatTasa(cov.tasa) : '', cod_riesgo: cov.cod_riesgo || '', nombre: cov.nombre || (cov.Cobertura ? String(cov.Cobertura) : ''), deducible: (cov.deducible !== undefined && cov.deducible !== null) ? cov.deducible : 'SIN DEDUCIBLE', cod_deducible: cov.cod_deducible || '' }); setCoberturasError(''); setErroresCoberturaValidacion({}); setModalCoberturaOpen(true); };

  const guardarCobertura = () => {
    const planIdx = Number(planSeleccionado);
    if (isNaN(planIdx) || planIdx < 0 || planIdx >= planes.length) return;

    const baseRaw = (nuevaCobertura && nuevaCobertura._raw) ? { ...nuevaCobertura._raw } : {};
    const coberturaParaValidar = {
      ...baseRaw,
      ...nuevaCobertura,
      cod_riesgo: (nuevaCobertura.cod_riesgo || baseRaw.cod_riesgo || ''),
      nombre: (nuevaCobertura.nombre || baseRaw.nombre || (baseRaw.Cobertura ? String(baseRaw.Cobertura) : '')),
    };

    let errores = {};
    if (!coberturaParaValidar.nombre || coberturaParaValidar.nombre.trim() === '') {
      errores.cobertura = tipoEmision === 'personalizable' ? 'Ingrese el nombre de la cobertura' : 'Seleccione una cobertura';
    }

    const sumaOk = toNumber(nuevaCobertura.sumaAsegurada, NaN);
    if (!isFinite(sumaOk) || sumaOk <= 0) {
      errores.sumaAsegurada = 'Debe ser mayor a 0';
    }

    const primaOk = toNumber(nuevaCobertura.prima, NaN);
    if (!isFinite(primaOk) || primaOk <= 0) {
      errores.prima = 'Debe ser mayor a 0';
    }

    if (Object.keys(errores).length > 0) {
      setErroresCoberturaValidacion(errores);
      return;
    }
    setErroresCoberturaValidacion({});

    const updated = [...planes];
    const lista = [...(updated[planIdx].coberturas || [])];
    const cobertura = {
      ...coberturaParaValidar,
      sumaAsegurada: nuevaCobertura.sumaAsegurada,
      tasa: nuevaCobertura.tasa,
      prima: toNumber(nuevaCobertura.prima, 0),
      deducible: (nuevaCobertura.deducible !== undefined && nuevaCobertura.deducible !== null) ? nuevaCobertura.deducible : 'SIN DEDUCIBLE',
      cod_deducible: nuevaCobertura.cod_deducible || '',
      responsabilidad: 0,
      cod_ramo_tec: coberturaParaValidar.cod_ramo_tec || (ramo && ramo.value != null ? String(ramo.value) : ''),
      cod_subramo_tec: coberturaParaValidar.cod_subramo_tec || (subRamo && subRamo.value != null ? String(subRamo.value) : ''),
      sn_acumula_suma_total: coberturaParaValidar.sn_acumula_suma_total != null ? coberturaParaValidar.sn_acumula_suma_total : 0,
      sn_acumula_prima: coberturaParaValidar.sn_acumula_prima != null ? coberturaParaValidar.sn_acumula_prima : 0
    };
    delete cobertura.value; delete cobertura.label; delete cobertura._raw;

    const dupIdx = lista.findIndex(c => sameCoverageKey(c, cobertura));
    if (isEditCobertura) {
      if (dupIdx !== -1 && dupIdx !== indexCoberturaEdit) { setErroresCoberturaValidacion({ global: 'Esta cobertura ya está agregada en el plan.' }); return; }
      lista[indexCoberturaEdit] = cobertura;
    } else {
      if (dupIdx !== -1) { setErroresCoberturaValidacion({ global: 'Esta cobertura ya está agregada en el plan.' }); return; }
      lista.push(cobertura);
    }

    const cobTotal = lista.reduce((sum, c) => sum + toNumber(c.prima, 0), 0);
    updated[planIdx] = { ...updated[planIdx], coberturas: lista, primaNeta: cobTotal.toFixed(2), primaTotal: (cobTotal * 1.04).toFixed(2) };
    setPlanes(updated);
    setNuevaCobertura({ cod_riesgo: '', nombre: '', sumaAsegurada: '', tasa: '', prima: '', deducible: 'SIN DEDUCIBLE', cod_deducible: '2' }); setIsEditCobertura(false); setIndexCoberturaEdit(null); setModalCoberturaOpen(false);
  };

  const eliminarCobertura = (planIdx, covIdx) => {
    const updated = planes.map((p, i) => {
      if (i !== planIdx) return p;
      const lista = (p.coberturas || []).filter((_, ci) => ci !== covIdx);
      const cobTotal = lista.reduce((sum, c) => sum + toNumber(c.prima, 0), 0);
      return { ...p, coberturas: lista, primaNeta: cobTotal.toFixed(2), primaTotal: (cobTotal * 1.04).toFixed(2) };
    });
    setPlanes(updated);
  };

  /* ===================== Asistencias (abrir/guardar/eliminar) ===================== */
  const abrirModalAgregarAsistencia = (planIdx = null) => { if (!isMultiple) setPlanSeleccionado(planIdx); setIsEditAsistencia(false); setIndexAsistenciaEdit(null); setNuevaAsistencia({ codigo: '', nombre: '', valor: '' }); setErroresAsistenciaValidacion({}); setModalAsistenciaOpen(true); };
  const abrirModalEditarAsistencia = (planIdx, asIdx) => { let item; if (isMultiple) { item = (asistenciasGlobal || [])[asIdx]; } else { item = (planes[planIdx].asistencias || [])[asIdx]; setPlanSeleccionado(planIdx); } setIsEditAsistencia(true); setIndexAsistenciaEdit(asIdx); setNuevaAsistencia({ codigo: (item && item.codigo) || '', nombre: (item && item.nombre) || '', valor: (item && item.prima) != null ? String(item.prima) : '' }); setErroresAsistenciaValidacion({}); setModalAsistenciaOpen(true); };

  const guardarAsistencia = () => {
    let errores = {};
    if (!nuevaAsistencia.nombre || !nuevaAsistencia.codigo) {
      errores.asistencia = 'Seleccione una asistencia';
    }

    const valorOk = toNumber(nuevaAsistencia.valor, NaN);
    if (!isFinite(valorOk) || valorOk < 0 || nuevaAsistencia.valor === '') {
      errores.valor = 'Debe ingresar un monto válido';
    }

    if (Object.keys(errores).length > 0) {
      setErroresAsistenciaValidacion(errores);
      return;
    }
    setErroresAsistenciaValidacion({});

    if (isMultiple) {
      const lista = [...asistenciasGlobal];
      const asistencia = { codigo: nuevaAsistencia.codigo || '', nombre: nuevaAsistencia.nombre, prima: toNumber(nuevaAsistencia.valor, 0) };

      const dupIdx = lista.findIndex(a => a.codigo === asistencia.codigo);
      if (isEditAsistencia) {
        if (dupIdx !== -1 && dupIdx !== indexAsistenciaEdit) { setErroresAsistenciaValidacion({ global: 'Esta asistencia ya está agregada al plan.' }); return; }
        lista[indexAsistenciaEdit] = asistencia;
      } else {
        if (dupIdx !== -1) { setErroresAsistenciaValidacion({ global: 'Esta asistencia ya está agregada al plan.' }); return; }
        lista.push(asistencia);
      }
      setAsistenciasGlobal(lista);
    } else {
      const planIdx = Number(planSeleccionado); if (isNaN(planIdx) || planIdx < 0 || planIdx >= planes.length) return;
      const updated = [...planes];
      const lista = [...(updated[planIdx].asistencias || [])];
      const asistencia = { codigo: nuevaAsistencia.codigo || '', nombre: nuevaAsistencia.nombre, prima: toNumber(nuevaAsistencia.valor, 0) };

      const dupIdx = lista.findIndex(a => a.codigo === asistencia.codigo);
      if (isEditAsistencia) {
        if (dupIdx !== -1 && dupIdx !== indexAsistenciaEdit) { setErroresAsistenciaValidacion({ global: 'Esta asistencia ya está agregada al plan.' }); return; }
        lista[indexAsistenciaEdit] = asistencia;
      } else {
        if (dupIdx !== -1) { setErroresAsistenciaValidacion({ global: 'Esta asistencia ya está agregada al plan.' }); return; }
        lista.push(asistencia);
      }
      updated[planIdx].asistencias = lista; setPlanes(updated);
    }
    setNuevaAsistencia({ codigo: '', nombre: '', valor: '' }); setIsEditAsistencia(false); setIndexAsistenciaEdit(null); setModalAsistenciaOpen(false);
  };

  const eliminarAsistencia = (planIdx, asIdx) => {
    if (isMultiple) { const lista = [...asistenciasGlobal]; lista.splice(asIdx, 1); setAsistenciasGlobal(lista); }
    else { const updated = [...planes]; const lista = [...(updated[planIdx].asistencias || [])]; lista.splice(asIdx, 1); updated[planIdx].asistencias = lista; setPlanes(updated); }
  };

  /* ===================== Validación ===================== */
  const validar = () => {
    let valid = true;
    const newErrors = [];
    const edadErrors = {};

    // Validate global age range
    const edadMin = Number(edadMinimaGlobal);
    const edadMax = Number(edadMaximaGlobal);
    if (!edadMinimaGlobal || isNaN(edadMin) || edadMin < 1 || edadMin > 99) {
      edadErrors.edadMinima = 'Edad mínima debe ser entre 1 y 99';
      valid = false;
    }
    if (!edadMaximaGlobal || isNaN(edadMax) || edadMax < edadMin || edadMax > 99) {
      edadErrors.edadMaxima = 'Edad máxima debe ser mayor o igual a la mínima y no mayor a 99';
      valid = false;
    }
    setErroresEdad(edadErrors);

    planes.forEach((plan, i) => {
      const err = {};
      if (!plan.nombre || plan.nombre.trim().length === 0) {
        err.nombre = 'Campo requerido';
      } else if (plan.nombre.trim().length < 3) {
        err.nombre = 'Ingrese un nombre válido';
      }
      if (plan.primaNeta === '' || isNaN(Number(plan.primaNeta))) err.primaNeta = 'Debe ser un número válido';

      // Product mode: must have a product selected and coverages loaded
      if (isProductoMode) {
        if (!plan.producto || String(plan.producto).trim() === '') {
          err.producto = 'Seleccione un producto';
          valid = false;
        }
        if (!plan.coberturas || plan.coberturas.length === 0) {
          err.coberturas = 'No existen coberturas configuradas para el producto seleccionado';
          valid = false;
        }
      }

      // Nueva regla: si dependeDeOtroGratuidad === 'si', debe indicar afectaAPlanGratuidad
      if (i > 0 && plan.dependeDeOtroGratuidad === 'si') {
        if (!plan.tipoCondicional) err.tipoCondicional = 'Seleccione tipo';
        if (!plan.valorCondicional || isNaN(plan.valorCondicional) || Number(plan.valorCondicional) <= 0) {
          err.valorCondicional = 'Debe ser un número válido';
        } else if (plan.tipoCondicional === 'porcentaje' && Number(plan.valorCondicional) > 100) {
          err.valorCondicional = 'El porcentaje no puede ser mayor a 100';
        }
        if (!plan.afectaAPlanGratuidad && plan.afectaAPlanGratuidad !== 0) err.afectaAPlanGratuidad = 'Seleccione el plan afectado';
        const idxAfecta = Number(plan.afectaAPlanGratuidad);
        if (isNaN(idxAfecta) || idxAfecta < 0 || idxAfecta >= i) err.afectaAPlanGratuidad = 'Debe afectar a un plan anterior';
      }
      // Nueva regla: si dependeDeOtroAplica === 'si', debe indicar afectaAPlanAplica
      if (i > 0 && plan.dependeDeOtroAplica === 'si') {
        if (!plan.afectaAPlanAplica && plan.afectaAPlanAplica !== 0) err.afectaAPlanAplica = 'Seleccione el plan afectado';
        const idxAfecta = Number(plan.afectaAPlanAplica);
        if (isNaN(idxAfecta) || idxAfecta < 0 || idxAfecta >= i) err.afectaAPlanAplica = 'Debe afectar a un plan anterior';
      }

      if (Object.keys(err).length > 0) valid = false; newErrors[i] = err;
    });
    setErrors(newErrors); return valid;
  };

  const canAddPlan = isMultiple && planes[0] && (
    tipoEmision === 'personalizable'
      ? (planes[0].nombre && planes[0].nombre.trim().length >= 3)
      : (
          (Array.isArray(planes[0].coberturas) && planes[0].coberturas.length > 0) ||
          (planes[0].producto && String(planes[0].producto).trim() !== '' && toNumber(planes[0].primaNeta) > 0)
        )
  );

  /* ===================== Render ===================== */
  return (
    <div>
      <Col md="12" className="text-end mt-3">
        {showAlert && (
          <Alert color="danger" toggle={() => setShowAlert(false)}>
            Hay errores en el formulario. Revisa los campos marcados en rojo.
          </Alert>
        )}
      </Col>

      {/* ===== Edad global (aplica a todos los planes) ===== */}
      <Card className="mb-3" style={{ borderColor: '#001871' }}>
        <CardBody>
          <h6 style={{ color: '#001871', marginBottom: 12 }}>Rango de edad (aplica a todos los planes)</h6>
          <Row>
            <Col md="6">
              <Label>Edad mínima</Label>
              <input
                type="number"
                className={`form-control${erroresEdad.edadMinima ? ' is-invalid' : ''}`}
                min={1}
                max={99}
                value={edadMinimaGlobal || ''}
                onChange={e => handleEdadGlobalChange(e.target.value, edadMaximaGlobal || '')}
                placeholder="Ej: 18"
              />
              {erroresEdad.edadMinima && <small style={{ color: 'red' }}>{erroresEdad.edadMinima}</small>}
            </Col>
            <Col md="6">
              <Label>Edad máxima</Label>
              <input
                type="number"
                className={`form-control${erroresEdad.edadMaxima ? ' is-invalid' : ''}`}
                min={1}
                max={99}
                value={edadMaximaGlobal || ''}
                onChange={e => handleEdadGlobalChange(edadMinimaGlobal || '', e.target.value)}
                placeholder="Ej: 65"
              />
              {erroresEdad.edadMaxima && <small style={{ color: 'red' }}>{erroresEdad.edadMaxima}</small>}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {planes.map((plan, index) => (
        <Card key={index} className="mb-3">
          <CardBody>
            <Row className="align-items-center">
              <Col md="10">
                <h5 style={{ marginBottom: 0 }}>Plan {index + 1}: {plan.nombre} {index === 0 ? ' (Principal)' : ''}</h5>
              </Col>
              <Col md="1" className="text-end">
                {(planes.length > 1 || (planes.length === 1 && (plan.nombre || (plan.coberturas && plan.coberturas.length > 0) || (plan.asistencias && plan.asistencias.length > 0) || (isMultiple && asistenciasGlobal.length > 0)))) && (
                  <Button color="danger" title={planes.length === 1 ? "Resetear plan" : "Eliminar plan"} style={{ width: 35, height: 35, padding: 0, borderRadius: '50%' }} onClick={() => eliminarPlan(index)}>
                    <i className={planes.length === 1 ? "fa fa-eraser" : "fa fa-trash"} />
                  </Button>
                )}
              </Col>
              <Col md="1" className="text-end">
                <Button style={{ backgroundColor: '#001871', color: '#fff', border: 'none', width: 35, height: 35, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }} onClick={() => toggleCollapse(index)} title={collapseStates[index] ? 'Contraer' : 'Expandir'}>
                  <i className={`fa ${collapseStates[index] ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </Button>
              </Col>
            </Row>

            <Collapse isOpen={collapseStates[index]}>
              <div className="mt-3">
                <Row>
                  <Col md="12">
                    <Label>Nombre del plan</Label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength={45}
                      value={plan.nombre}
                      onChange={(e) => handleChange(index, 'nombre', e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, ''))}
                      placeholder="Ingrese el nombre del plan"
                    />
                    {errors[index] && errors[index].nombre && <small style={{ color: 'red' }}>{errors[index].nombre}</small>}
                  </Col>

                  {/* Per-plan product selector (product mode only) */}
                  {isProductoMode && (
                    <Col md="12" className="mt-3">
                      <Label>Producto <span className="text-danger">*</span></Label>
                      <Input
                        type="select"
                        value={plan.producto || ''}
                        onChange={(e) => handleChange(index, 'producto', e.target.value)}
                        className={errors[index] && errors[index].producto ? 'is-invalid' : ''}
                      >
                        <option value="">Seleccione un producto</option>
                        {(productosOptions || []).map(p => (
                          <option key={String(p.value)} value={String(p.value)}>{p.label}</option>
                        ))}
                      </Input>
                      {errors[index] && errors[index].producto && (
                        <small style={{ color: 'red' }}>{errors[index].producto}</small>
                      )}
                    </Col>
                  )}

                  {/* Per-plan loading indicator (product mode) */}
                  {isProductoMode && planCoberturasLoading[index] && (
                    <Col md="12" className="mt-2">
                      <div className="d-flex align-items-center text-muted">
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Cargando coberturas del producto...
                      </div>
                    </Col>
                  )}

                  {/* Per-plan no-coverage alert (product mode) */}
                  {isProductoMode && !planCoberturasLoading[index] && planCoberturasError[index] && (
                    <Col md="12" className="mt-2">
                      <Alert color="warning" className="mb-0 py-2">
                        <i className="fa fa-exclamation-triangle me-2" />
                        {planCoberturasError[index]}
                      </Alert>
                    </Col>
                  )}
                  {errors[index] && errors[index].coberturas && (
                    <Col md="12" className="mt-1">
                      <small style={{ color: 'red' }}>{errors[index].coberturas}</small>
                    </Col>
                  )}

                  {/* ===== Nueva dependencia general por plan (solo si NO es el primero) ===== */}
                  {index > 0 && (
                    <>
                      <Col md="4" className="mt-3">
                        <Label>¿Este plan depende de otro para gratuidad?</Label>
                        <Input type="select" value={plan.dependeDeOtroGratuidad || 'no'} onChange={(e) => handleChange(index, 'dependeDeOtroGratuidad', e.target.value)}>
                          <option value="no">No</option>
                          <option value="si">Sí</option>
                        </Input>
                      </Col>

                      {plan.dependeDeOtroGratuidad === 'si' && (<>
                        <Col md="4" className="mt-3">
                          <Label>Tipo de condición</Label>
                          <Input
                            type="select"
                            value={plan.tipoCondicional || ''}
                            onChange={(e) => handleChange(index, 'tipoCondicional', e.target.value)}
                          >
                            <option value="">Seleccione</option>
                            <option value="porcentaje">Porcentaje</option>
                            <option value="cantidad">Cantidad</option>
                          </Input>
                          {errors[index] && errors[index].tipoCondicional && (
                            <small style={{ color: 'red' }}>{errors[index].tipoCondicional}</small>
                          )}
                        </Col>

                        <Col md="4" className="mt-3">
                          <Label>
                            {plan.tipoCondicional === 'porcentaje' ? 'Valor (%)' : 'Valor (cantidad)'}
                          </Label>
                          <input
                            type="number"
                            className="form-control"
                            value={plan.valorCondicional || ''}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (val) {
                                if (plan.tipoCondicional === 'porcentaje') {
                                  if (Number(val) > 100) val = '100';
                                } else {
                                  const parts = val.split('.');
                                  if (parts[0].length > 8) {
                                    parts[0] = parts[0].slice(0, 8);
                                    val = parts.join('.');
                                  }
                                }
                              }
                              handleChange(index, 'valorCondicional', val);
                            }}
                            min="0"
                            step="0.01"
                            placeholder={plan.tipoCondicional === 'porcentaje' ? 'Ej: 10' : 'Ej: 50.00'}
                          />
                          {errors[index] && errors[index].valorCondicional && (
                            <small style={{ color: 'red' }}>{errors[index].valorCondicional}</small>
                          )}
                        </Col>
                        <Col md="8" className="mt-3">
                          <Label>Seleccione el plan afectado</Label>
                          <Input type="select" value={plan.afectaAPlanGratuidad || ''} onChange={(e) => handleChange(index, 'afectaAPlanGratuidad', e.target.value)}>
                            <option value="">Seleccione</option>
                            {planes
                              .map((p, i) => ({ value: String(i), label: `Plan ${i + 1}${p.nombre ? `: ${p.nombre}` : ''}` }))
                              .filter(opt => Number(opt.value) < index)
                              .map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                          </Input>
                          {errors[index] && errors[index].afectaAPlanGratuidad && <small style={{ color: 'red' }}>{errors[index].afectaAPlanGratuidad}</small>}
                        </Col>
                      </>)}
                    </>
                  )}

                  {index > 0 && (
                    <>
                      <Col md="4" className="mt-3">
                        <Label>¿Este plan depende de otro para su aplicación?</Label>
                        <Input type="select" value={plan.dependeDeOtroAplica || 'no'} onChange={(e) => handleChange(index, 'dependeDeOtroAplica', e.target.value)}>
                          <option value="no">No</option>
                          <option value="si">Sí</option>
                        </Input>
                      </Col>

                      {plan.dependeDeOtroAplica === 'si' && (
                        <Col md="8" className="mt-3">
                          <Label>Seleccione el plan afectado</Label>
                          <Input type="select" value={plan.afectaAPlanAplica || ''} onChange={(e) => handleChange(index, 'afectaAPlanAplica', e.target.value)}>
                            <option value="">Seleccione</option>
                            {planes
                              .map((p, i) => ({ value: String(i), label: `Plan ${i + 1}${p.nombre ? `: ${p.nombre}` : ''}` }))
                              .filter(opt => Number(opt.value) < index)
                              .map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                          </Input>
                          {errors[index] && errors[index].afectaAPlanAplica && <small style={{ color: 'red' }}>{errors[index].afectaAPlanAplica}</small>}
                        </Col>
                      )}
                    </>
                  )}

                  <Col md="12" className="text-end mt-3">
                    {tipoEmision === 'personalizable' && (
                      <Button style={{ marginLeft: 10, backgroundColor: '#001871', color: 'white', border: 'none' }} onClick={() => abrirModalAgregarCobertura(index)}>
                        + Agregar cobertura
                      </Button>
                    )}
                    {!isMultiple && (
                      <Button style={{ marginLeft: 10, backgroundColor: '#0d6efd', color: 'white', border: 'none' }} onClick={() => abrirModalAgregarAsistencia(index)}>
                        + Agregar asistencia
                      </Button>
                    )}
                  </Col>

                  {/* Coberturas */}
                  {plan.coberturas && plan.coberturas.length > 0 && (
                    <Col md="12" className="mt-2">
                      <h6>Coberturas</h6>
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Deducible</th>
                            <th>Suma Asegurada</th>
                            <th>Tasa Comercial</th>
                            <th>Prima Neta Anual</th>
                            <th style={{ width: 160 }}>Opciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.coberturas.map((c, i) => (
                            <tr key={i}>
                              <td>{c.nombre}</td>
                              <td>{(c.deducible !== undefined && c.deducible !== null) ? c.deducible : 'SIN DEDUCIBLE'}</td>
                              <td>{c.sumaAsegurada}</td>
                              <td>{formatTasa(c.tasa)}</td>
                              <td>${toNumber(c.prima, 0).toFixed(2)}</td>
                              
                                <td>
                                  <Button color="warning" size="sm" className="me-2" onClick={() => abrirModalEditarCobertura(index, i)}>
                                    <i className="fa fa-pencil" /> Editar
                                  </Button>
                                  {!isProductoMode && (
                                  <Button color="danger" size="sm" onClick={() => eliminarCobertura(index, i)}>
                                    <i className="fa fa-trash" /> Eliminar
                                  </Button>
                                  )}
                                </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Col>
                  )}

                  {/* Asistencias por plan (solo individual) */}
                  {!isMultiple && plan.asistencias && plan.asistencias.length > 0 && (
                    <Col md="12" className="mt-4">
                      <h6>Asistencias</h6>
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Código</th>
                            <th>Valor</th>
                            <th style={{ width: 160 }}>Opciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {plan.asistencias.map((a, i) => (
                            <tr key={i}>
                              <td>{a.nombre}</td>
                              <td>{a.codigo || '-'}</td>
                              <td>${toNumber(a.prima, 0).toFixed(2)}</td>
                              <td>
                                <Button color="warning" size="sm" className="me-2" onClick={() => abrirModalEditarAsistencia(index, i)}>
                                  <i className="fa fa-pencil" /> Editar
                                </Button>
                                <Button color="danger" size="sm" onClick={() => eliminarAsistencia(index, i)}>
                                  <i className="fa fa-trash" /> Eliminar
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </Col>
                  )}

                  {/* Totales del plan */}
                  <Col md="12" className="mt-3">
                    <Label>Prima neta mensual por persona (solo coberturas)</Label>
                    <input type="number" className="form-control" value={plan.primaNeta} placeholder="Prima neta coberturas" disabled />
                    {errors[index] && errors[index].primaNeta && (<small style={{ color: 'red' }}>{errors[index].primaNeta}</small>)}
                  </Col>
                  <Col md="12">
                    <Label>Prima total x persona (coberturas × 1.04)</Label>
                    <input type="number" className="form-control" value={plan.primaTotal || ''} placeholder="Se calcula como (suma coberturas) × 1.04" disabled />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </CardBody>
        </Card>
      ))}

      {/* Agregar plan */}
      <div className="text-end mb-3 flex" style={{ flexDirection: 'row', justifyContent: 'end' }}>
        {isMultiple && (
          <Button style={{ marginLeft: 10, backgroundColor: '#001871', color: 'white', border: 'none' }} onClick={agregarPlan} disabled={!canAddPlan} title={canAddPlan ? '' : tipoEmision === 'personalizable' ? 'Ingresa el nombre del primer plan para habilitar' : 'Agrega al menos una cobertura en el primer plan para habilitar'}>
            + Agregar otro plan
          </Button>
        )}
      </div>

      {/* Asistencias globales (múltiple) */}
      {isMultiple && (
        <Card className="mb-4" style={{ borderColor: '#0d6efd' }}>
          <CardBody>
            <Row className="align-items-center">
              <Col md="8"><h5 style={{ color: '#0d6efd', marginBottom: 0 }}>Asistencias (aplican a todos los planes)</h5></Col>
              <Col md="4" className="text-end">
                <Button style={{ backgroundColor: '#0d6efd', color: 'white', border: 'none' }} onClick={() => abrirModalAgregarAsistencia()}>
                  + Agregar asistencia
                </Button>
              </Col>
            </Row>
            {asistenciasGlobal.length > 0 ? (
              <div className="mt-3">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Código</th>
                      <th>Valor</th>
                      <th style={{ width: 160 }}>Opciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asistenciasGlobal.map((a, i) => (
                      <tr key={i}>
                        <td>{a.nombre}</td>
                        <td>{a.codigo || '-'}</td>
                        <td>${toNumber(a.prima, 0).toFixed(2)}</td>
                        <td>
                          <Button color="warning" size="sm" className="me-2" onClick={() => abrirModalEditarAsistencia(null, i)}>
                            <i className="fa fa-pencil" /> Editar
                          </Button>
                          <Button color="danger" size="sm" onClick={() => eliminarAsistencia(null, i)}>
                            <i className="fa fa-trash" /> Eliminar
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mt-3"><small className="text-muted">No hay asistencias agregadas.</small></div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Resumen global */}
      <Card className="mb-4" style={{ borderColor: '#001871' }}>
        <CardBody>
          <h5 style={{ color: '#001871', marginBottom: 16 }}>Resumen de todos los planes</h5>
          <Row>
            <Col md="4" className="mb-3">
              <Label>Subtotal mensual x persona</Label>
              <input type="number" className="form-control" value={resumenPlanes.subtotal} disabled />
              <small className="text-muted">Subtotal = (Σ coberturas con 4%) + (Σ asistencias).</small>
            </Col>
            <Col md="4" className="mb-3">
              <Label>Impuestos 4% </Label>
              <input type="number" className="form-control" value={resumenPlanes.impuestos} disabled />
            </Col>
            <Col md="4" className="mb-3">
              <Label>Total mensual x persona</Label>
              <input type="number" className="form-control" value={resumenPlanes.total} disabled />
              <small className="text-muted">Total = Subtotal + 4% de (coberturas + asistencias).</small>
            </Col>
          </Row>

          <div className="mt-2">
            <Button color="link" onClick={() => setImpuestosOpen(!impuestosOpen)} style={{ textDecoration: 'none', paddingLeft: 0 }} title={impuestosOpen ? 'Ocultar desglose de impuestos' : 'Ver desglose de impuestos'}>
              {impuestosOpen ? 'Ocultar' : 'Ver'} desglose de impuestos (visualización)
            </Button>
            <Collapse isOpen={impuestosOpen}>
              <div className="mt-2">
                <Row>
                  <Col md="3" className="mb-2">
                    <Label>3.5% Superintendencia</Label>
                    <input type="number" className="form-control" value={impuestosDetalle.superintendencia35} disabled />
                  </Col>
                  <Col md="3" className="mb-2">
                    <Label>0.5% Seguro campesino</Label>
                    <input type="number" className="form-control" value={impuestosDetalle.campesino05} disabled />
                  </Col>
                  <Col md="3" className="mb-2">
                    <Label>Total 4%</Label>
                    <input type="number" className="form-control" value={impuestosDetalle.total4} disabled />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </div>
        </CardBody>
      </Card>

      {/* ====== Modales separados ====== */}
      <CoberturaModal
        isOpen={modalCoberturaOpen}
        toggle={() => setModalCoberturaOpen(false)}
        isEdit={isEditCobertura}
        nuevaCobertura={nuevaCobertura}
        setNuevaCobertura={setNuevaCobertura}
        coberturasOptions={coberturasOptions}
        coberturasLoading={coberturasLoading}
        coberturasError={coberturasError}
        deduciblesOptions={deduciblesOptions}
        onGuardar={guardarCobertura}
        setCoberturasError={setCoberturasError}
        erroresValidacion={erroresCoberturaValidacion}
        modoManual={tipoEmision === 'personalizable'}
      />

      <AsistenciaModal
        isOpen={modalAsistenciaOpen}
        toggle={() => setModalAsistenciaOpen(false)}
        isEdit={isEditAsistencia}
        nuevaAsistencia={nuevaAsistencia}
        setNuevaAsistencia={setNuevaAsistencia}
        asistenciasOptions={asistenciasOptions}
        asistenciasLoading={asistenciasLoading}
        asistenciasError={asistenciasError}
        erroresValidacion={erroresAsistenciaValidacion}
        onGuardar={guardarAsistencia}
      />
    </div>
  );
});

export default FormularioPlanes;
